import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import type { ProductGwpOfferViewModel, ProductInformationViewModel } from 'autogen/swagger/all';
import { GWPHeading, GwpFromValue, GwpValue } from 'autogen/translation-keys/trans-website-product-gwpoffer';
import { GeneralExceptionError } from 'autogen/translation-keys/trans-website-shared-error';
import ContentCard from 'components/ContentCard';
import { Typography } from 'components/generic/typography';
import { useQuickView } from 'components/quickView';
import QuickView from 'components/quickView/quickView';
import toast from 'components/Toast';
import { fetchHTML } from 'modules/helpers/fetch';
import { customScrollbar } from 'modules/helpers/mixins';
import { format, toHtmlContent } from 'modules/helpers/strings';

const QuickViewContainer = styled.div`
    padding-left: ${({ theme }) => theme.Spacing16};
    padding-right: ${({ theme }) => theme.Spacing16};

    ${customScrollbar};
`;

const Container = styled.div`
    display: grid;
    row-gap: ${({ theme }) => theme.Spacing8};
`;

export default function GWPOffer({
    gwpOfferModel,
    gwpOfferDetailsLinkAction,
    ...restProps
}: {
    gwpOfferModel: ProductGwpOfferViewModel;
    gwpOfferDetailsLinkAction: ProductInformationViewModel['gwpOfferDetailsLinkAction'];
}): JSX.Element {
    const { primaryImage, marketingValue, hasMultipleGwpProducts, title } = gwpOfferModel;
    const [quickViewContent, setQuickViewContent] = useState<string | null>(null);
    const { t } = useTranslation();
    const { toggle } = useQuickView('GWPOffer');

    const overlineText = hasMultipleGwpProducts
        ? format(t(GwpFromValue))(marketingValue)
        : format(t(GwpValue))(marketingValue);

    const onClickHandler = () => {
        fetchHTML(`${gwpOfferDetailsLinkAction.url}`)
            .then((html) => {
                setQuickViewContent(html);
                toggle(true);
            })
            .catch(() => {
                toast.error(t(GeneralExceptionError));
            });
    };

    return (
        <>
            <Container {...restProps}>
                <Typography.Body size="L" strong>
                    {t(GWPHeading)}
                </Typography.Body>
                <ContentCard.Horizontal
                    image={{ src: primaryImage.url, alt: '' }}
                    heading={title}
                    subheading={marketingValue ? overlineText : undefined}
                    onClickWrapper={onClickHandler}
                    type="default"
                    showBorder
                />
            </Container>

            <QuickView quickViewId="GWPOffer" position="right">
                <QuickViewContainer>{toHtmlContent(quickViewContent)}</QuickViewContainer>
            </QuickView>
        </>
    );
}
